import React, { useState, useEffect, useRef } from 'react';
import { Btn, Card, Chip, STitle, Spinner } from './ui';
import { C, CIVS, DIFFS, buildPrompt, DIFF_TIME, DIFF_POINTS } from '../data/constants';
import {
  createDuel, sendChallenge, acceptChallenge, declineChallenge, cancelChallenge,
  joinDuel, fetchDuel, submitDuelScore, subscribeDuel,
  subscribeIncomingChallenges, subscribeLobbyPresence,
} from '../lib/db';
import { isConfigured } from '../lib/supabase';

const API_URL = 'https://api.anthropic.com/v1/messages';
const API_KEY = process.env.REACT_APP_ANTHROPIC || '';

const SEEN_MAX = 300;
function loadSeenQuestions(civKey) {
  try { const r = localStorage.getItem(`kabaak_seen_${civKey}`); return r ? JSON.parse(r) : []; } catch { return []; }
}
function saveSeenQuestion(civKey, text) {
  try {
    const existing = loadSeenQuestions(civKey);
    if (existing.includes(text)) return;
    const updated = [...existing, text];
    if (updated.length > SEEN_MAX) updated.splice(0, updated.length - SEEN_MAX);
    localStorage.setItem(`kabaak_seen_${civKey}`, JSON.stringify(updated));
  } catch {}
}

async function callClaude(prompt, attempt = 0) {
  const delay = ms => new Promise(r => setTimeout(r, ms));
  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 1200,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    if (data.error) {
      // Rate limit — back off and retry
      if ((data.error.type === 'rate_limit_error' || res.status === 429) && attempt < 3) {
        await delay(1500 * (attempt + 1));
        return callClaude(prompt, attempt + 1);
      }
      throw new Error(data.error.message);
    }
    const text = data.content[0].text;
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) throw new Error('Invalid response format');
    const q = JSON.parse(match[0]);
    const correctText = q.options[q.correct];
    const shuffled = [...q.options];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    q.options = shuffled;
    q.correct = shuffled.indexOf(correctText);
    return q;
  } catch (e) {
    if (attempt < 2 && !e.message?.includes('Not enough')) {
      await delay(1000 * (attempt + 1));
      return callClaude(prompt, attempt + 1);
    }
    throw e;
  }
}

async function loadDuelQuestions(civKey, diffKey, onProgress) {
  const TARGET = 5;
  const seenTexts = loadSeenQuestions(civKey).slice(-20);
  // Fire all fetches in parallel; track progress as each resolves
  const fetches = Array.from({ length: TARGET }, (_, i) =>
    callClaude(buildPrompt(civKey, diffKey, seenTexts)).then(q => {
      seenTexts.push(q.question);
      saveSeenQuestion(civKey, q.question);
      onProgress(i + 1);
      return q;
    })
  );
  const results = await Promise.allSettled(fetches);
  const questions = results.filter(r => r.status === 'fulfilled').map(r => r.value);
  if (questions.length < 3) throw new Error('Not enough questions generated. Please try again.');
  return questions;
}

// ── PHASES: lobby | generating | challenging | waiting | quiz | done ──────────

export default function Duel({ user, player, onHome, onSaveResult, onDuelWin, initialJoinCode = '', initialChallenge = null, autoCreate = false, initialCiv = 'all', initialDiff = 'apprentice' }) {
  const [phase, setPhase]           = useState('lobby');
  const [role, setRole]             = useState(null);
  const [selCiv, setSelCiv]         = useState(initialCiv);
  const [selDiff, setSelDiff]       = useState(initialDiff);
  const [duel, setDuel]             = useState(null);
  const [duelCode, setDuelCode]     = useState('');
  const [joinCode, setJoinCode]     = useState(initialJoinCode);
  const [genProgress, setGenProg]   = useState(0);
  const [genError, setGenError]     = useState('');
  const [joinError, setJoinError]   = useState('');
  const [showCodeJoin, setShowCodeJoin] = useState(false);

  const [onlinePlayers, setOnlinePlayers]         = useState([]);
  const [challengeTarget, setChallengeTarget]     = useState(null);
  const [incomingChallenge, setIncomingChallenge] = useState(null);

  const [qIdx, setQIdx]         = useState(0);
  const [timeLeft, setTime]     = useState(0);
  const [locked, setLocked]     = useState(false);
  const [picked, setPicked]     = useState(null);
  const [myScore, setMyScore]   = useState(0);
  const [myStreak, setMyStreak] = useState(0);
  const [oppData, setOppData]   = useState(null);

  const phaseRef      = useRef('lobby');
  const roleRef       = useRef(null);
  const lockedRef     = useRef(false);
  const winDoneRef    = useRef(false);
  const timerRef      = useRef(null);
  const unsubRef      = useRef(null);
  const stateRef      = useRef({ score: 0, streak: 0, best: 0, answers: [] });
  const autoJoinedRef    = useRef(false);
  const autoAcceptedRef  = useRef(false);
  const autoCreatedRef   = useRef(false);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { roleRef.current = role; }, [role]);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (unsubRef.current) unsubRef.current();
  }, []);

  useEffect(() => {
    if (!user || !isConfigured) return;
    const username = player?.username || 'Scholar';
    const score = player?.totalScore || 0;

    const unsubPresence  = subscribeLobbyPresence(user.id, username, score, setOnlinePlayers);
    const unsubIncoming  = subscribeIncomingChallenges(user.id, (challenge) => {
      if (phaseRef.current === 'lobby') {
        setIncomingChallenge(challenge);
      }
    }, ':duel');
    return () => { unsubPresence(); unsubIncoming(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (initialJoinCode && user && !autoJoinedRef.current) {
      autoJoinedRef.current = true;
      handleJoinWithCode(initialJoinCode.trim().toUpperCase());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (initialChallenge && user && !autoAcceptedRef.current) {
      autoAcceptedRef.current = true;
      autoAcceptChallenge(initialChallenge);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (autoCreate && user && !autoCreatedRef.current) {
      autoCreatedRef.current = true;
      handleCreateDuel();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!incomingChallenge?.id || !isConfigured) return;
    const unsub = subscribeDuel(incomingChallenge.id, (updated) => {
      if (updated.status !== 'pending') setIncomingChallenge(null);
    });
    const timeout = setTimeout(() => setIncomingChallenge(null), 60000);
    return () => { unsub(); clearTimeout(timeout); };
  }, [incomingChallenge?.id]);

  useEffect(() => {
    if (!duel?.id) return;
    if (unsubRef.current) unsubRef.current();

    unsubRef.current = subscribeDuel(duel.id, (updated) => {
      setDuel(d => ({ ...d, ...updated }));

      if ((phaseRef.current === 'waiting' || phaseRef.current === 'challenging') && updated.status === 'active') {
        setPhase('quiz');
      }

      if (phaseRef.current === 'challenging' && updated.status === 'declined') {
        setGenError(`${challengeTarget?.username || 'Player'} declined your challenge.`);
        setPhase('lobby');
        setDuel(null);
      }

      const oppScore   = roleRef.current === 'host' ? updated.guest_score   : updated.host_score;
      const oppCorrect = roleRef.current === 'host' ? updated.guest_correct : updated.host_correct;
      if (oppScore != null) {
        setOppData({ score: oppScore, correct: oppCorrect });
        if (phaseRef.current === 'done' && !winDoneRef.current) {
          winDoneRef.current = true;
          if (stateRef.current.score > oppScore) onDuelWin?.();
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duel?.id]);

  useEffect(() => {
    if (phase !== 'quiz' || !duel?.questions) return;
    clearInterval(timerRef.current);
    const total = DIFF_TIME[duel.diff] || 40;
    setTime(total);
    setLocked(false);
    setPicked(null);
    lockedRef.current = false;
    let rem = total;
    timerRef.current = setInterval(() => {
      rem--;
      setTime(rem);
      if (rem <= 0) { clearInterval(timerRef.current); if (!lockedRef.current) handleAnswer(-1); }
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, qIdx]);

  async function handleChallenge(target) {
    if (!user) { setGenError('Sign in to challenge players.'); return; }
    setChallengeTarget(target);
    setGenError('');
    setPhase('generating');
    setGenProg(0);
    try {
      const questions = await loadDuelQuestions(selCiv, selDiff, setGenProg);
      const username  = player?.username || 'Host';
      const code      = await sendChallenge(user.id, username, target.userId, selCiv, selDiff, questions);
      const duelData  = await fetchDuel(code);
      setDuel(duelData);
      setDuelCode(code);
      setRole('host');
      setPhase('challenging');
    } catch (e) {
      setGenError(e.message || 'Failed to send challenge.');
      setPhase('lobby');
      setChallengeTarget(null);
    }
  }

  async function handleCreateDuel() {
    if (!user) { setGenError('Sign in to create a duel.'); return; }
    setPhase('generating');
    setGenProg(0);
    setGenError('');
    try {
      const questions = await loadDuelQuestions(selCiv, selDiff, setGenProg);
      const username  = player?.username || 'Host';
      const code      = await createDuel(user.id, username, selCiv, selDiff, questions);
      const duelData  = await fetchDuel(code);
      setDuel(duelData);
      setDuelCode(code);
      setRole('host');
      setPhase('waiting');
    } catch (e) {
      setGenError(e.message || 'Failed to create duel.');
      setPhase('lobby');
    }
  }

  async function handleJoinWithCode(code) {
    if (!user) { setJoinError('Sign in to join a duel.'); return; }
    setJoinError('');
    try {
      const username = player?.username || 'Challenger';
      await joinDuel(code, user.id, username);
      const duelData = await fetchDuel(code);
      if (!duelData?.questions?.length) throw new Error('Duel has no questions yet.');
      setDuel(duelData);
      setRole('guest');
      setPhase('quiz');
    } catch (e) {
      setJoinError(e.message || 'Could not join. Check the code and try again.');
    }
  }

  async function handleJoinDuel() {
    const code = joinCode.trim().toUpperCase();
    if (!code) { setJoinError('Enter a duel code.'); return; }
    await handleJoinWithCode(code);
  }

  async function autoAcceptChallenge(challenge) {
    if (!user) return;
    try {
      const username = player?.username || 'Challenger';
      await acceptChallenge(challenge.id, user.id, username);
      const duelData = await fetchDuel(challenge.code);
      if (!duelData?.questions?.length) throw new Error('Duel has no questions.');
      setDuel(duelData);
      setRole('guest');
      setPhase('quiz');
    } catch (e) {
      setGenError(e.message || 'Could not join the challenge.');
      setPhase('lobby');
    }
  }

  async function handleAcceptChallenge() {
    if (!incomingChallenge || !user) return;
    try {
      const username = player?.username || 'Challenger';
      await acceptChallenge(incomingChallenge.id, user.id, username);
      const duelData = await fetchDuel(incomingChallenge.code);
      if (!duelData?.questions?.length) throw new Error('Duel has no questions.');
      setDuel(duelData);
      setRole('guest');
      setIncomingChallenge(null);
      setPhase('quiz');
    } catch (e) {
      setIncomingChallenge(null);
    }
  }

  async function handleDeclineChallenge() {
    if (incomingChallenge?.id) await declineChallenge(incomingChallenge.id);
    setIncomingChallenge(null);
  }

  async function handleCancelChallenge() {
    if (duel?.id) await cancelChallenge(duel.id);
    resetDuel();
  }

  function handleAnswer(idx) {
    if (lockedRef.current || !duel?.questions) return;
    lockedRef.current = true;
    clearInterval(timerRef.current);
    setLocked(true);
    setPicked(idx);

    const q       = duel.questions[qIdx];
    const elapsed = (DIFF_TIME[duel.diff] || 40) - timeLeft;
    const ok      = idx !== -1 && idx === q.correct;
    const st      = stateRef.current;

    st.answers.push({
      correct: ok, q: q.question, options: q.options,
      correctIndex: q.correct, pickedIndex: idx, correctAnswer: q.options?.[q.correct],
    });

    if (ok) {
      st.streak++;
      if (st.streak > st.best) st.best = st.streak;
      const bonus = Math.max(0, (DIFF_TIME[duel.diff] || 40) - elapsed);
      st.score += (DIFF_POINTS[duel.diff] || 10) + Math.round(bonus * 1.5);
      setMyScore(st.score);
      setMyStreak(st.streak);
    } else {
      st.streak = 0;
      setMyStreak(0);
    }

    setTimeout(() => advanceQuestion(), 2200);
  }

  async function advanceQuestion() {
    const next = qIdx + 1;
    if (next >= duel.questions.length) {
      await finishDuel();
    } else {
      setQIdx(next);
    }
  }

  async function finishDuel() {
    const st           = stateRef.current;
    const correctCount = st.answers.filter(a => a.correct).length;

    onSaveResult?.({
      score: st.score, correct: correctCount, total: st.answers.length,
      civ: duel.civ, diff: duel.diff, bestStreak: st.best,
    });

    try {
      await submitDuelScore(duel.id, role, st.score, correctCount);
      const updated = await fetchDuel(duel.code);
      if (updated) {
        setDuel(updated);
        const oppScore = role === 'host' ? updated.guest_score : updated.host_score;
        const oppCorr  = role === 'host' ? updated.guest_correct : updated.host_correct;
        if (oppScore != null && !winDoneRef.current) {
          winDoneRef.current = true;
          setOppData({ score: oppScore, correct: oppCorr });
          if (st.score > oppScore) onDuelWin?.();
        }
      }
    } catch {}

    setPhase('done');
  }

  async function copyCode() {
    try {
      const shareText = `⚔️ Challenge me on KABAAK!\n\nDuel code: ${duelCode}\nGo to ${window.location.origin} and enter the code to play!`;
      if (navigator.share) {
        await navigator.share({ title: 'KABAAK Duel', text: shareText });
      } else {
        await navigator.clipboard.writeText(duelCode);
      }
    } catch {}
  }

  function resetDuel() {
    setPhase('lobby');
    setDuel(null);
    setRole(null);
    setQIdx(0);
    setMyScore(0);
    setMyStreak(0);
    setOppData(null);
    setChallengeTarget(null);
    winDoneRef.current = false;
    stateRef.current = { score: 0, streak: 0, best: 0, answers: [] };
  }

  const q           = duel?.questions?.[qIdx];
  const totalQs     = duel?.questions?.length || 5;
  const diffTotal   = DIFF_TIME[duel?.diff] || 40;
  const timerPct    = timeLeft / diffTotal;
  const timerColor  = timerPct > 0.5 ? '#00D4A8' : timerPct > 0.25 ? '#FF9900' : '#FF5A3C';
  const oppName     = role === 'host' ? (duel?.guest_username || 'Opponent') : (duel?.host_username || 'Host');
  const st          = stateRef.current;
  const finalCorrect = st.answers.filter(a => a.correct).length;

  const IncomingOverlay = incomingChallenge && phase !== 'quiz' && (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 28, maxWidth: 320, width: '100%', textAlign: 'center', border: `3px solid ${C.amber}`, boxShadow: '0 8px 0 #C07000' }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>⚔️</div>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>Challenge!</div>
        <div style={{ fontSize: 14, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
          <strong style={{ color: C.text }}>{incomingChallenge.host_username}</strong> challenged you to a duel!
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
          {(() => {
            const c = CIVS.find(c => c.key === incomingChallenge.civ);
            const d = DIFFS.find(d => d.key === incomingChallenge.diff);
            return <><Chip type="or">{c?.icon} {c?.label}</Chip><Chip type="tl">{d?.icon} {d?.label}</Chip></>;
          })()}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="green" onClick={handleAcceptChallenge} style={{ flex: 1 }}>Accept</Btn>
          <Btn variant="white" onClick={handleDeclineChallenge} style={{ flex: 1 }}>Decline</Btn>
        </div>
      </div>
    </div>
  );

  if (phase === 'lobby') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: 16 }}>
      {IncomingOverlay}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={onHome} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted, padding: '0 12px 0 0' }}>←</button>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900 }}>⚔️ Live Duel</div>
          <div style={{ fontSize: 12, color: C.muted }}>Challenge anyone online right now</div>
        </div>
      </div>

      {!isConfigured && (
        <div style={{ background: '#FFF0EC', border: `2px solid #FFB0A0`, borderRadius: 14, padding: 14, marginBottom: 14, fontSize: 13, color: C.coral, lineHeight: 1.6 }}>
          ⚙️ Configure Supabase to enable live duels.
        </div>
      )}
      {isConfigured && !user && (
        <div style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 14, padding: 14, marginBottom: 14, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
          🔑 <strong>Sign in first</strong> to duel — go to Profile → Sign In.
        </div>
      )}

      {genError && (
        <div style={{ background: '#FFF0EC', border: `2px solid #FFB0A0`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: C.coral }}>
          ⚠️ {genError}
        </div>
      )}

      <STitle>Civilization</STitle>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 10 }}>
        {CIVS.map(c => {
          const active = selCiv === c.key;
          return (
            <div key={c.key} onClick={() => setSelCiv(c.key)}
              style={{ flexShrink: 0, minWidth: 54, background: active ? c.bg : '#fff', border: `2px solid ${active ? c.border : C.border}`, borderRadius: 10, padding: '8px 4px', textAlign: 'center', cursor: 'pointer', boxShadow: `0 2px 0 ${active ? c.shadow : C.border}` }}>
              <div style={{ fontSize: 16, marginBottom: 1 }}>{c.icon}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: active ? c.text : C.muted }}>{c.label}</div>
            </div>
          );
        })}
      </div>

      <STitle>Difficulty</STitle>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {DIFFS.map(d => {
          const active = selDiff === d.key;
          return (
            <div key={d.key} onClick={() => setSelDiff(d.key)}
              style={{ flex: 1, background: active ? d.activeBg : '#fff', border: `2px solid ${active ? d.activeBorder : C.border}`, borderRadius: 10, padding: '8px 4px', textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ fontSize: 14 }}>{d.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: active ? d.activeText : C.muted }}>{d.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <STitle style={{ marginBottom: 0 }}>Players Online</STitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.teal, fontWeight: 700 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.teal }} />
          {onlinePlayers.length} online
        </div>
      </div>

      {onlinePlayers.length === 0 ? (
        <div style={{ background: '#F5F0EE', borderRadius: 14, padding: '18px 16px', textAlign: 'center', marginBottom: 16, color: C.muted, fontSize: 13, lineHeight: 1.6, border: `2px solid ${C.border}` }}>
          No one else is on the Duel screen right now.<br />
          Share a code with a friend below ↓
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {onlinePlayers.map(p => (
            <div key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `2px solid ${C.border}`, borderRadius: 14, padding: '12px 14px', marginBottom: 8, boxShadow: `0 2px 0 ${C.border}` }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#E8F8F0', border: `2px solid ${C.teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👤</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.username}</div>
                <div style={{ fontSize: 11, color: C.muted }}>⭐ {(p.score || 0).toLocaleString()}</div>
              </div>
              <button
                onClick={() => handleChallenge(p)}
                disabled={!user || !isConfigured}
                style={{ padding: '9px 16px', background: C.teal, color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, fontFamily: 'Roboto, sans-serif', opacity: (!user || !isConfigured) ? 0.45 : 1, boxShadow: '0 2px 0 #00A882' }}>
                Challenge
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => setShowCodeJoin(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 13, fontWeight: 600, padding: '4px 0', marginBottom: 8, fontFamily: 'Roboto, sans-serif' }}>
        <span>{showCodeJoin ? '▾' : '▸'}</span>
        Play with a friend (use a code)
      </button>

      {showCodeJoin && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Create a room and share the code:</div>
            <Btn variant="green" onClick={handleCreateDuel} style={{ opacity: (!user || !isConfigured) ? 0.45 : 1 }}>
              🎮 Create Room
            </Btn>
          </div>
          <div style={{ borderTop: `2px solid ${C.border}`, paddingTop: 12 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Got a code from a friend?</div>
            <input
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase())}
              placeholder="6-DIGIT CODE"
              maxLength={6}
              style={{ width: '100%', padding: 12, background: '#fff', border: `2px solid ${C.border}`, borderRadius: 12, fontSize: 18, fontWeight: 900, fontFamily: 'monospace', letterSpacing: '.2em', textAlign: 'center', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
            />
            {joinError && <div style={{ color: C.coral, fontSize: 12, marginBottom: 8 }}>⚠️ {joinError}</div>}
            <Btn variant="orange" onClick={handleJoinDuel} style={{ opacity: (!user || !isConfigured) ? 0.45 : 1 }}>
              ⚔️ Join with Code
            </Btn>
          </div>
        </Card>
      )}
      <div style={{ height: 20 }} />
    </div>
  );

  if (phase === 'generating') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
      {IncomingOverlay}
      <div className="wobble" style={{ fontSize: 60, marginBottom: 16 }}>🌍</div>
      <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>
        {challengeTarget ? `Challenging ${challengeTarget.username}…` : 'Preparing your duel…'}
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>Generating {genProgress}/5 questions</div>
      <div style={{ width: 220, height: 10, background: '#F0E8D8', borderRadius: 5, overflow: 'hidden', border: '2px solid #E0D0C0', marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${(genProgress / 5) * 100}%`, background: `linear-gradient(90deg,${C.teal},${C.teal2})`, borderRadius: 3, transition: 'width .4s' }} />
      </div>
      <div style={{ fontSize: 11, color: C.muted }}>Takes about 5–10 seconds…</div>
    </div>
  );

  if (phase === 'challenging') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>⚔️</div>
      <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Challenge Sent!</div>
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 8 }}>
        Waiting for <strong style={{ color: C.text }}>{challengeTarget?.username || 'opponent'}</strong> to accept…
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 13, marginBottom: 28 }}>
        <Spinner /> Waiting…
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(() => {
          const c = CIVS.find(c => c.key === selCiv);
          const d = DIFFS.find(d => d.key === selDiff);
          return <><Chip type="or">{c?.icon} {c?.label}</Chip><Chip type="tl">{d?.icon} {d?.label}</Chip></>;
        })()}
      </div>
      <Btn variant="white" onClick={handleCancelChallenge} style={{ maxWidth: 200 }}>← Cancel</Btn>
    </div>
  );

  if (phase === 'waiting') return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
      {IncomingOverlay}
      <div style={{ fontSize: 44, marginBottom: 12 }}>⚔️</div>
      <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>Room Created!</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>Share this code with your opponent</div>

      <div style={{ background: '#FFF9E8', border: `3px solid ${C.amber}`, borderRadius: 20, padding: '18px 36px', marginBottom: 16, boxShadow: '0 4px 0 #C07000', cursor: 'pointer' }} onClick={copyCode}>
        <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: '.25em', color: C.text, fontFamily: 'monospace' }}>{duelCode}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Tap to copy / share</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 13, marginBottom: 20 }}>
        <Spinner /> Waiting for opponent to join…
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
        {(() => {
          const c = CIVS.find(c => c.key === selCiv);
          const d = DIFFS.find(d => d.key === selDiff);
          return <><Chip type="or">{c?.icon} {c?.label}</Chip><Chip type="tl">{d?.icon} {d?.label}</Chip></>;
        })()}
      </div>

      <Btn variant="white" onClick={onHome} style={{ maxWidth: 200 }}>← Cancel</Btn>
    </div>
  );

  if (phase === 'quiz' && duel?.questions) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ background: '#fff', padding: '10px 16px', borderBottom: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ flex: 1, height: 8, background: '#F0E8D8', borderRadius: 4, overflow: 'hidden', border: '2px solid #E0D0C0' }}>
          <div style={{ height: '100%', width: `${(qIdx / totalQs) * 100}%`, background: `linear-gradient(90deg,${C.teal},${C.teal2})`, borderRadius: 3, transition: 'width .4s' }} />
        </div>
        <Chip type="or">⭐ {myScore}</Chip>
        {myStreak >= 2 && <Chip type="gr">🔥 {myStreak}</Chip>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', background: '#FFF9E8', borderBottom: `2px solid ${C.border}`, padding: '8px 16px', gap: 10, flexShrink: 0 }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, textTransform: 'uppercase' }}>You</div>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.amber }}>{myScore}</div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 900, color: C.muted }}>VS</div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase' }}>{oppName}</div>
          <div style={{ fontSize: 16, fontWeight: 900, color: C.muted }}>{oppData != null ? oppData.score : '…'}</div>
        </div>
      </div>

      <div className="scroll-area" style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fff', border: `3px solid ${timerColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{timeLeft}</span>
          </div>
          <div style={{ flex: 1, height: 8, background: '#F0E8D8', borderRadius: 4, overflow: 'hidden', border: '2px solid #E0D0C0' }}>
            <div style={{ height: '100%', width: `${timerPct * 100}%`, background: timerColor, borderRadius: 3, transition: 'width 1s linear' }} />
          </div>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{qIdx + 1}/{totalQs}</span>
        </div>

        {q && (
          <div className="fade-up">
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
              {q.category && <Chip type="or">{q.category}</Chip>}
              {q.era      && <Chip type="bl">{q.era}</Chip>}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1.45, marginBottom: 16 }}>{q.question}</div>
            {(q.options || []).map((opt, i) => {
              const isCorrect = locked && i === q.correct;
              const isWrong   = locked && i === picked && i !== q.correct;
              return (
                <div key={i} onClick={() => !locked && handleAnswer(i)}
                  style={{ width: '100%', textAlign: 'left', padding: '13px 15px', marginBottom: 9, borderRadius: 13, color: C.text, fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 11, cursor: locked ? 'default' : 'pointer', background: isCorrect ? '#E8F8F0' : isWrong ? '#FFF0EC' : '#fff', border: `2px solid ${isCorrect ? C.green : isWrong ? C.coral : C.border}`, boxShadow: `0 2px 0 ${isCorrect ? '#1A7A40' : isWrong ? '#C03020' : C.border}`, pointerEvents: locked ? 'none' : 'auto', transition: 'all .12s' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: isCorrect ? C.green : isWrong ? C.coral : '#F5EFE5', border: `2px solid ${isCorrect ? C.green : isWrong ? C.coral : C.border}`, color: (isCorrect || isWrong) ? '#fff' : C.muted }}>
                    {'ABCD'[i]}
                  </div>
                  <span>{opt}</span>
                </div>
              );
            })}
            {locked && q.fact && (
              <div style={{ marginTop: 12, background: '#FFFBF0', border: '2px solid #FFD080', borderRadius: 14, padding: '12px 14px', display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.65, fontWeight: 500 }}>{q.fact}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (phase === 'done') {
    const myFinal = st.score;
    const waiting = oppData == null;
    const won     = !waiting && myFinal > oppData.score;
    const tied    = !waiting && myFinal === oppData.score;

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '20px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div className="pop" style={{ fontSize: 64, marginBottom: 8 }}>
            {waiting ? '⏳' : won ? '🏆' : tied ? '🤝' : '💪'}
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 4, color: C.text }}>
            {waiting ? 'Score Submitted!' : won ? 'You Won!' : tied ? "It's a Tie!" : 'Opponent Won'}
          </div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
            {waiting ? 'Your score is locked in — waiting for their result…'
              : won ? 'Outstanding knowledge of African history! 🌍'
              : tied ? 'Two equally matched scholars!'
              : 'Keep studying — the ancestors are watching 📜'}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          <Card style={{ textAlign: 'center', border: `2px solid ${C.amber}`, background: '#FFF9E8' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>You</div>
            <div style={{ fontSize: 34, fontWeight: 900, color: C.amber }}>{myFinal}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{finalCorrect}/{totalQs} correct</div>
          </Card>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{oppName}</div>
            <div style={{ fontSize: 34, fontWeight: 900, color: waiting ? '#CCC' : C.text }}>
              {waiting ? '?' : oppData.score}
            </div>
            {!waiting && <div style={{ fontSize: 11, color: C.muted }}>{oppData.correct}/{totalQs} correct</div>}
            {waiting && <div style={{ fontSize: 11, color: C.muted }}>Still playing…</div>}
          </Card>
        </div>

        {won && (
          <div style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 14, padding: 14, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 28 }}>🦁</div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Duel Win!</div>
              <div style={{ fontSize: 13, color: C.text }}>Win 3 duels total to earn the Lion of Aksum title.</div>
            </div>
          </div>
        )}

        <Btn variant="green" onClick={onHome}>🏠 Back to Home</Btn>
        <div style={{ height: 8 }} />
        <Btn variant="white" onClick={resetDuel}>⚔️ Duel Again</Btn>
        <div style={{ height: 20 }} />
      </div>
    );
  }

  return null;
}
